import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft, ChevronRight, FileText, Undo2, CheckCircle2, Clock, MinusCircle,
  ArrowUp, ArrowDown, ArrowUpDown, Mail,
} from 'lucide-react'
import { MailExportDialog } from '@/components/mail/MailExportDialog'
import { sectionTitle } from '@/lib/form-styles'
import { downloadFile } from '@/lib/download'
import { apiErrorText } from '@/lib/api-error'
import { monthlyReportApi } from '../api/monthly-report.api'
import type {
  MonthlyReportOverview,
  MonthlyReportOverviewRow,
  MonthlyReportRowStatus,
} from '../api/monthly-report.api'
import { bestaetige } from '@/lib/dialoge'

/**
 * AP-20: Manager-Abgabeübersicht.
 *
 * Zeigt pro Monat, welche Mitarbeiter berichtspflichtig sind und wer bereits
 * abgegeben hat. MANAGER+ können die Berichtspflicht je Mitarbeiter setzen,
 * das abgegebene PDF ansehen und eine Abgabe widerrufen (gibt den Monat frei).
 */

type StatusFilter = 'all' | MonthlyReportRowStatus

const STATUS_META: Record<MonthlyReportRowStatus, { label: string; cls: string; Icon: typeof Clock }> = {
  submitted: { label: 'Abgegeben', cls: 'text-green-700 dark:text-green-400', Icon: CheckCircle2 },
  open: { label: 'Offen', cls: 'text-amber-600 dark:text-amber-400', Icon: Clock },
  not_required: { label: 'Nicht pflichtig', cls: 'text-muted-foreground', Icon: MinusCircle },
}

type SortKey = 'name' | 'status' | 'submittedAt' | 'required' | 'timeSource'
// Offen zuerst (Manager-Fokus), dann Abgegeben, dann Nicht pflichtig.
const STATUS_RANK: Record<MonthlyReportRowStatus, number> = { open: 0, submitted: 1, not_required: 2 }
const PAGE_SIZE = 25

function submittedTime(r: MonthlyReportOverviewRow): number {
  return r.submission?.status === 'SUBMITTED' && r.submission.submittedAt
    ? Date.parse(r.submission.submittedAt)
    : 0
}

function compareRows(a: MonthlyReportOverviewRow, b: MonthlyReportOverviewRow, key: SortKey): number {
  switch (key) {
    case 'status': return STATUS_RANK[a.status] - STATUS_RANK[b.status]
    case 'submittedAt': return submittedTime(a) - submittedTime(b)
    case 'required': return (a.required ? 1 : 0) - (b.required ? 1 : 0)
    case 'timeSource': return a.timeSource.localeCompare(b.timeSource)
    case 'name':
    default: return a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
  }
}

export default function MonthlyReportOverviewPage() {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [data, setData] = useState<MonthlyReportOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [busyUser, setBusyUser] = useState<string | null>(null)
  // AP-10 Nachtrag 5: eine Abgabe per E-Mail — ein Bericht je Mail.
  const [mailZeile, setMailZeile] = useState<{ id: string; name: string } | null>(null)
  const { t } = useTranslation()
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' })
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await monthlyReportApi.overview(year, month)
      setData(res.data)
    } catch (err) {
      setError(apiErrorText(err, 'Übersicht konnte nicht geladen werden.'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [year, month])

  useEffect(() => {
    void load()
  }, [load])

  const shiftMonth = (dir: -1 | 1) => {
    let m = month + dir
    let y = year
    if (m < 1) { m = 12; y -= 1 }
    if (m > 12) { m = 1; y += 1 }
    setMonth(m)
    setYear(y)
  }

  const toggleRequired = async (row: MonthlyReportOverviewRow, required: boolean) => {
    setBusyUser(row.userId)
    setError(null)
    // Optimistisch aktualisieren
    setData((d) =>
      d ? { ...d, rows: d.rows.map((r) => (r.userId === row.userId ? { ...r, required } : r)) } : d,
    )
    try {
      await monthlyReportApi.setRequired(row.userId, required)
      await load() // Status (offen/nicht pflichtig) + Zähler neu berechnen
    } catch (err) {
      setError(apiErrorText(err, 'Berichtspflicht konnte nicht gespeichert werden.'))
      await load() // Zurücksetzen auf Serverstand
    } finally {
      setBusyUser(null)
    }
  }

  const viewPdf = async (submissionId: string, name: string) => {
    try {
      const res = await monthlyReportApi.submissionPdf(submissionId)
      const filename = `Monatsbericht_${name}_${year}-${String(month).padStart(2, '0')}.pdf`
      downloadFile(res.data as ArrayBuffer, filename, 'application/pdf')
    } catch (err) {
      setError(apiErrorText(err, 'PDF konnte nicht geladen werden.'))
    }
  }

  const revoke = async (row: MonthlyReportOverviewRow) => {
    if (!row.submission) return
    if (!(await bestaetige(`Abgabe von ${row.name} für ${data?.period.label} widerrufen? Der Monat wird dann wieder zur Bearbeitung freigegeben.`))) return
    setBusyUser(row.userId)
    setError(null)
    try {
      await monthlyReportApi.revoke(row.submission.id)
      await load()
    } catch (err) {
      setError(apiErrorText(err, 'Widerruf fehlgeschlagen.'))
    } finally {
      setBusyUser(null)
    }
  }

  // Auf Monats-/Filter-/Sortierwechsel zurück auf Seite 1.
  useEffect(() => { setPage(1) }, [year, month, filter, sort])

  const filteredSorted = useMemo(() => {
    if (!data) return []
    const base = filter === 'all' ? data.rows : data.rows.filter((r) => r.status === filter)
    const sign = sort.dir === 'asc' ? 1 : -1
    return [...base].sort((a, b) => {
      const c = compareRows(a, b, sort.key)
      return c !== 0 ? c * sign : a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
    })
  }, [data, filter, sort])

  const total = filteredSorted.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const pageSafe = Math.min(page, totalPages)
  const paged = useMemo(
    () => filteredSorted.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE),
    [filteredSorted, pageSafe],
  )

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-6">
      {/* Kopf + Monatsnavigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className={sectionTitle}>Monatsberichte — Abgaben</h1>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftMonth(-1)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted" aria-label="Vorheriger Monat">
            <ChevronLeft size={20} />
          </button>
          <span className="min-w-[9.5rem] text-center text-base font-medium text-foreground">
            {data?.period.label ?? '…'}
          </span>
          <button onClick={() => shiftMonth(1)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted" aria-label="Nächster Monat">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Zähler + Filter */}
      {data && (
        <div className="flex flex-wrap items-center gap-2">
          {([
            ['all', `Alle ${data.counts.total}`],
            ['open', `Offen ${data.counts.open}`],
            ['submitted', `Abgegeben ${data.counts.submitted}`],
            ['not_required', 'Nicht pflichtig'],
          ] as [StatusFilter, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                filter === key
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-input text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="ml-auto text-sm text-muted-foreground">
            {data.counts.submitted} von {data.counts.required} pflichtigen abgegeben
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-muted-foreground">Lädt…</div>
      ) : !data || total === 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 py-10 text-center text-sm text-muted-foreground">
          Keine Mitarbeiter in dieser Ansicht.
        </div>
      ) : (
        <>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                {([
                  ['name', 'Mitarbeiter', 'text-left'],
                  ['status', 'Status', 'text-left'],
                  ['submittedAt', 'Abgegeben am', 'text-left'],
                  ['required', 'Berichtspflicht', 'text-center'],
                  ['timeSource', 'Stunden aus', 'text-left'],
                ] as [SortKey, string, string][]).map(([key, label, align]) => (
                  <th key={key} className={`px-3 py-2 font-semibold ${align}`}>
                    <button
                      onClick={() => toggleSort(key)}
                      className={`inline-flex items-center gap-1 hover:text-foreground ${align === 'text-center' ? 'mx-auto' : ''}`}
                    >
                      {label}
                      {sort.key === key
                        ? (sort.dir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />)
                        : <ArrowUpDown size={13} className="opacity-40" />}
                    </button>
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => {
                const meta = STATUS_META[r.status]
                const busy = busyUser === r.userId
                return (
                  <tr key={r.userId} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-medium text-foreground">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 ${meta.cls}`}>
                        <meta.Icon size={15} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-foreground">
                      {r.submission?.status === 'SUBMITTED' && r.submission.submittedAt
                        ? new Date(r.submission.submittedAt).toLocaleDateString(undefined, {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                          })
                        : '–'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={r.required}
                        disabled={busy}
                        onChange={(e) => toggleRequired(r, e.target.checked)}
                        className="h-4 w-4 accent-[hsl(var(--primary))]"
                        aria-label={`Berichtspflicht für ${r.name}`}
                      />
                    </td>
                    {/* AP-24: Woher die Stunden dieses Mitarbeiters kommen.
                        Ohne eigene Übersteuerung gedämpft — dann gilt der
                        Mandanten-Standard und eine Änderung dort wirkt mit. */}
                    <td className="px-3 py-2">
                      <span className={r.timeSourceOverridden ? 'text-foreground' : 'text-muted-foreground'}>
                        {r.timeSource === 'MANUAL' ? 'Händisch' : 'Zeiterfassung'}
                        {!r.timeSourceOverridden && ' (Standard)'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {r.submission?.status === 'SUBMITTED' && (
                          <>
                            <button
                              onClick={() => viewPdf(r.submission!.id, r.name)}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-foreground hover:bg-muted"
                              title="Abgegebenes PDF ansehen"
                            >
                              <FileText size={14} /> PDF
                            </button>
                            <button
                              onClick={() => setMailZeile({ id: r.submission!.id, name: r.name })}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-foreground hover:bg-muted"
                              title={t('mailExport.menu')}
                            >
                              <Mail size={14} /> {t('mailExport.short')}
                            </button>
                            <button
                              onClick={() => revoke(r)}
                              disabled={busy}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-amber-600 hover:bg-amber-500/10 disabled:opacity-40"
                              title="Abgabe widerrufen (gibt den Monat wieder frei)"
                            >
                              <Undo2 size={14} /> Widerrufen
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-1 text-sm text-muted-foreground">
            <span>
              {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, total)} von {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pageSafe <= 1}
                className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 hover:bg-muted disabled:opacity-40"
              >
                <ChevronLeft size={15} /> Zurück
              </button>
              <span className="tabular-nums">Seite {pageSafe}/{totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={pageSafe >= totalPages}
                className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 hover:bg-muted disabled:opacity-40"
              >
                Weiter <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
        </>
      )}

      {mailZeile && (
        <MailExportDialog
          open
          onClose={() => setMailZeile(null)}
          source="MONTHLY_REPORT_SUBMISSION"
          title={t('mailExport.titles.MONTHLY_REPORT')}
          defaultSubject={[t('mailExport.titles.MONTHLY_REPORT'), mailZeile.name, data?.period.label].filter(Boolean).join(' ')}
          request={() => monthlyReportApi.submissionPdfRequest(mailZeile.id)}
          formats={['pdf']}
        />
      )}
    </div>
  )
}
